import { getApiUrl } from '../config/environment';

export interface ProxmoxHost {
  id: number;
  name: string;
  hostname: string;
}

export interface FirewallRule {
  id: number;
  proxmox_host_id: number;
  direction: 'IN' | 'OUT';
  action: 'ACCEPT' | 'DROP' | 'REJECT';
  protocol: 'tcp' | 'udp' | 'icmp' | 'any';
  port: string;
  source_ip?: string;
  dest_ip?: string;
  comment?: string;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface FirewallStatus {
  enabled: boolean;
  status: string;
  rules_count: number;
  raw_config: string;
  fail2ban?: {
    enabled: boolean;
    jails: string[];
    banned_ips: string[];
  };
}

export interface CreateFirewallRuleData {
  direction: 'IN' | 'OUT';
  action: 'ACCEPT' | 'DROP' | 'REJECT';
  protocol: 'tcp' | 'udp' | 'icmp' | 'any';
  port: string;
  source_ip?: string;
  dest_ip?: string;
  comment?: string;
  is_active?: boolean;
  priority?: number;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Get all Proxmox hosts for dropdown
export const getProxmoxHosts = async (): Promise<ProxmoxHost[]> => {
  const response = await fetch(`${getApiUrl()}/api/admin/proxmox-host-firewall/hosts`, {
    headers: getAuthHeaders()
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.message);
  return data.hosts;
};

// Get firewall status for a host
export const getFirewallStatus = async (hostId: number): Promise<FirewallStatus> => {
  const response = await fetch(`${getApiUrl()}/api/admin/proxmox-host-firewall/hosts/${hostId}/status`, {
    headers: getAuthHeaders()
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.message);
  return data.status;
};

// Get all firewall rules for a host
export const getFirewallRules = async (hostId: number): Promise<FirewallRule[]> => {
  const response = await fetch(`${getApiUrl()}/api/admin/proxmox-host-firewall/hosts/${hostId}/rules`, {
    headers: getAuthHeaders()
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.message);
  return data.rules;
};

// Create a new firewall rule
export const createFirewallRule = async (hostId: number, rule: CreateFirewallRuleData): Promise<FirewallRule> => {
  const response = await fetch(`${getApiUrl()}/api/admin/proxmox-host-firewall/hosts/${hostId}/rules`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(rule)
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.message);
  return data.rule;
};

// Update a firewall rule
export const updateFirewallRule = async (ruleId: number, updates: Partial<CreateFirewallRuleData>): Promise<FirewallRule> => {
  const response = await fetch(`${getApiUrl()}/api/admin/proxmox-host-firewall/rules/${ruleId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates)
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.message);
  return data.rule;
};

// Delete a firewall rule
export const deleteFirewallRule = async (ruleId: number): Promise<void> => {
  const response = await fetch(`${getApiUrl()}/api/admin/proxmox-host-firewall/rules/${ruleId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.message);
};

// Toggle a firewall rule
export const toggleFirewallRule = async (ruleId: number): Promise<FirewallRule> => {
  const response = await fetch(`${getApiUrl()}/api/admin/proxmox-host-firewall/rules/${ruleId}/toggle`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.message);
  return data.rule;
};

// Apply all rules to host
export const applyFirewallRules = async (hostId: number): Promise<{ success: boolean; message: string; applied_count: number }> => {
  const response = await fetch(`${getApiUrl()}/api/admin/proxmox-host-firewall/hosts/${hostId}/apply`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.message);
  return data;
};

// Sync rules from Proxmox server to database
export const syncRulesFromProxmox = async (hostId: number): Promise<{ success: boolean; message: string; rules_synced: number }> => {
  const response = await fetch(`${getApiUrl()}/api/admin/proxmox-host-firewall/hosts/${hostId}/sync`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.message);
  return data;
};

export default {
  getProxmoxHosts,
  getFirewallStatus,
  getFirewallRules,
  createFirewallRule,
  updateFirewallRule,
  deleteFirewallRule,
  toggleFirewallRule,
  applyFirewallRules,
  syncRulesFromProxmox
};
